import React, { useCallback, useEffect, useState } from "react";
import api, { formatRupiah, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Boxes, Package, Plus, Pencil, Trash2, Save } from "lucide-react";

export default function ProductsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/products");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing({ nama: "", harga: "", deskripsi: "", syarat: [""] });
  }
  function openEdit(p) {
    setEditing({ id: p.id, nama: p.nama, harga: String(p.harga || 0), deskripsi: p.deskripsi || "", syarat: p.syarat?.length ? p.syarat : [""] });
  }
  async function save() {
    const body = {
      nama: editing.nama.trim(),
      harga: Number(editing.harga) || 0,
      deskripsi: editing.deskripsi || null,
      syarat: (editing.syarat || []).map(s => s.trim()).filter(Boolean),
    };
    if (!body.nama) return toast.error("Nama produk wajib diisi");
    try {
      if (editing.id) await api.put(`/products/${editing.id}`, body);
      else await api.post("/products", body);
      toast.success("Produk disimpan");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove(id) {
    try { await api.delete(`/products/${id}`); toast.success("Produk dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      <PageHeader
        overline="Master Data" title="Produk / Layanan"
        description="Kelola katalog produk beserta syarat & ketentuannya. Akan dipakai saat membuat SPH / Invoice."
        icon={Boxes} tone="sph"
        actions={<Button onClick={openCreate} className="bg-brand text-white hover:bg-brand-600" data-testid="btn-add-product"><Plus className="mr-1 h-4 w-4" /> Tambah Produk</Button>}
      />

      <Card className="overflow-hidden border-border shadow-none">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Nama</TableHead><TableHead className="text-right">Harga</TableHead><TableHead>Jumlah Syarat</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-ink-500">Belum ada produk.</TableCell></TableRow>}
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`row-product-${p.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-sky-100 text-sky-700"><Package className="h-4 w-4" /></div>
                    <div>
                      <div className="font-medium text-ink-900">{p.nama}</div>
                      {p.deskripsi && <div className="text-xs text-ink-500">{p.deskripsi}</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono">{formatRupiah(p.harga)}</TableCell>
                <TableCell>{p.syarat?.length || 0}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-prod-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`del-prod-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Hapus produk "{p.nama}"?</AlertDialogTitle><AlertDialogDescription>Tidak dapat diurungkan.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => remove(p.id)}>Hapus</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Ubah Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Nama Produk</Label><Input value={editing.nama} onChange={(e) => setEditing({ ...editing, nama: e.target.value })} data-testid="prod-nama" /></div>
              <div><Label>Harga Satuan (Rp)</Label><Input type="number" value={editing.harga} onChange={(e) => setEditing({ ...editing, harga: e.target.value })} data-testid="prod-harga" /></div>
              <div><Label>Deskripsi (opsional)</Label><Textarea rows={2} value={editing.deskripsi} onChange={(e) => setEditing({ ...editing, deskripsi: e.target.value })} data-testid="prod-deskripsi" /></div>
              <div>
                <Label>Syarat & Ketentuan</Label>
                <div className="space-y-2">
                  {editing.syarat.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <Input value={s} onChange={(e) => { const arr=[...editing.syarat]; arr[i]=e.target.value; setEditing({...editing,syarat:arr}); }} placeholder={`Syarat ke-${i+1}`} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => setEditing({...editing, syarat: editing.syarat.filter((_,j)=>j!==i)})}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setEditing({...editing, syarat: [...editing.syarat, ""]})}><Plus className="mr-1 h-3 w-3" /> Tambah Syarat</Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Batal</Button><Button onClick={save} className="bg-brand text-white" data-testid="save-prod"><Save className="mr-1 h-4 w-4" /> Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
